/**
 * Tracking GTM — events manuels (dataLayer GA4).
 *
 * Centralise les events poussés au chargement de page et via délégation :
 *   - view_item        (page produit)
 *   - view_item_list   (pages liste produits & résultats de recherche)
 *   - search           (page de résultats de recherche)
 *   - filter_use       (clic "Afficher les résultats")
 *   - compare          (ouverture du comparateur multi-véhicules)
 *   - faq              (ouverture d'une question FAQ)
 *
 * Les autres events manuels sont poussés au plus près de l'action :
 *   - select_item      -> assets/product-card.js
 *   - add_to_wishlist  -> assets/favoris-management.js
 *   - form_error       -> assets/form-lead.js / form-recovery.js / form-operation.js
 *   - submit_newsletter-> sections/footer.liquid
 *   - preload_data     -> layout/theme.liquid (avant GTM)
 *
 * Helpers (pushDataLayer, forgeVehicleItem, getCardVehicleJson) : assets/globals.js
 * Plan : documentations_techniques/plan-tracking-gtm.md
 */
(function () {
  "use strict";

  function onReady(fn) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn);
    } else {
      fn();
    }
  }

  /* ----- view_item (page produit) ----- */
  function trackViewItem() {
    var el = document.querySelector("script[data-product-json]");
    if (!el) return;
    var json;
    try {
      json = JSON.parse(el.textContent);
    } catch (e) {
      return;
    }
    var item = forgeVehicleItem(json);
    if (!item) return;
    pushDataLayer({
      event: "view_item",
      currency: "EUR",
      value: json.price,
      ecommerce: { items: [item] },
    });
  }

  /* ----- view_item_list (listes produits & recherche) ----- */
  function trackViewItemList() {
    var wrapper = document.querySelector(".products_list_wrapper");
    if (!wrapper) return;
    var listName = wrapper.getAttribute("data-list-name") || "";
    var seen = new Set();

    function pushBatch() {
      var cards = Array.prototype.slice.call(wrapper.querySelectorAll(".product_card"));
      var fresh = [];
      cards.forEach(function (card, i) {
        var json = getCardVehicleJson(card);
        if (!json || !json.sku || seen.has(json.sku)) return;
        seen.add(json.sku);
        fresh.push(forgeVehicleItem(json, { index: i + 1, item_list_name: listName }));
      });
      if (fresh.length) {
        pushDataLayer({
          event: "view_item_list",
          currency: "EUR",
          item_list_name: listName,
          ecommerce: { items: fresh },
        });
      }
    }

    pushBatch();

    // Détecte les cartes ajoutées ("afficher plus") ou un re-rendu complet
    // (changement de filtres en AJAX, qui retire puis réinjecte des cartes).
    var debounceTimer = null;
    var observer = new MutationObserver(function (mutations) {
      var replaced = mutations.some(function (m) {
        return Array.prototype.some.call(m.removedNodes, function (n) {
          return n.nodeType === 1 && n.classList && n.classList.contains("product_card");
        });
      });
      if (replaced) seen.clear();
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(pushBatch, 300);
    });
    observer.observe(wrapper, { childList: true });
  }

  /* ----- search (page de résultats de recherche) ----- */
  function trackSearch() {
    var page = document.querySelector("[data-search-results]");
    if (!page) return;
    var term = page.getAttribute("data-search-term") || "";
    var count = parseInt(page.getAttribute("data-results-count") || "0", 10);
    var cards = Array.prototype.slice.call(
      document.querySelectorAll(".products_list_wrapper .product_card"),
    );
    var items = [];
    cards.forEach(function (card, i) {
      var json = getCardVehicleJson(card);
      if (json) items.push(forgeVehicleItem(json, { index: i + 1 }));
    });
    pushDataLayer({
      event: "search",
      search_term: term,
      results_count: count,
      ecommerce: { items: items },
    });
  }

  /* ----- filter_use (clic "Afficher les résultats") ----- */
  // Sérialise les filtres sélectionnés au format
  //   categorie=val1&val2;categorie2=valA;budget=min-max
  function serializeFilters(form) {
    var groups = {};
    form
      .querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked')
      .forEach(function (input) {
        if (input.name === "sort_by" || input.name === "search") return;
        var categoryEl = input.closest(".filter_category");
        var key = input.name;
        if (categoryEl) {
          var labelEl = categoryEl.querySelector(".h4");
          if (labelEl) key = labelEl.textContent.trim().replace(/\s+/g, " ");
        }
        key = key.toLowerCase();
        (groups[key] = groups[key] || []).push(input.value);
      });

    // Fourchette de prix (poussée seulement si modifiée par rapport aux bornes)
    var priceMin = form.querySelector(".range_input_min");
    var priceMax = form.querySelector(".range_input_max");
    if (priceMin && priceMax) {
      var min = (priceMin.value || "").trim();
      var max = (priceMax.value || "").trim();
      var boundMin = priceMin.getAttribute("data-min");
      var boundMax = priceMax.getAttribute("data-max");
      if ((min && min !== boundMin) || (max && max !== boundMax)) {
        groups["budget"] = [min + "-" + max];
      }
    }

    return Object.keys(groups)
      .map(function (k) {
        return k + "=" + groups[k].join("&");
      })
      .join(";");
  }

  function trackFilterUse() {
    document.addEventListener("click", function (e) {
      var btn = e.target.closest(".filter_submit_button");
      if (!btn) return;
      var form = document.querySelector("filters-form form");
      if (!form) return;
      pushDataLayer({ event: "filter_use", filter_value: serializeFilters(form) });
    });
  }

  /* ----- compare (ouverture effective de la modale de comparaison) ----- */
  // On observe l'injection de la modale dans #modal_wrapper plutôt que le clic
  // bouton (le bouton est pointer-events:none tant qu'il y a < 2 véhicules, et
  // plusieurs déclencheurs existent desktop/mobile). C'est le moment où l'utilisateur
  // visualise réellement la comparaison.
  function pushCompare() {
    var moduleEl = document.querySelector("comparison-module");
    if (!moduleEl || typeof moduleEl.getVehicles !== "function") return;
    var ids = moduleEl.getVehicles();
    if (!ids || !ids.length) return;
    var items = [];
    ids.forEach(function (id, i) {
      var card = document.querySelector('.product_card[data-id="' + id + '"]');
      var json = getCardVehicleJson(card);
      if (json) items.push(forgeVehicleItem(json, { index: i + 1 }));
    });
    if (!items.length) return;
    pushDataLayer({ event: "compare", currency: "EUR", ecommerce: { items: items } });
  }

  function trackCompare() {
    var modalWrapper = document.getElementById("modal_wrapper");
    if (!modalWrapper) return;
    var observer = new MutationObserver(function () {
      var compareModal = modalWrapper.querySelector("comparison-list, #modal_compare_wrapper");
      if (!compareModal) return;
      // Évite un double push pour la même ouverture (le wrapper est ré-injecté à chaque ouverture)
      if (compareModal.getAttribute("data-gtm-compared") === "1") return;
      compareModal.setAttribute("data-gtm-compared", "1");
      pushCompare();
    });
    observer.observe(modalWrapper, { childList: true, subtree: true });
  }

  /* ----- faq (ouverture d'une question) ----- */
  // En phase capture : <collapsible-content> bascule data-open après notre handler,
  // donc data-open reflète encore l'état AVANT le clic.
  function trackFaq() {
    document.addEventListener(
      "click",
      function (e) {
        var title = e.target.closest(".faq .collapsible_title");
        if (!title) return;
        var collapsible = title.closest("collapsible-content");
        if (!collapsible) return;
        var wasOpen = collapsible.getAttribute("data-open") === "true";
        if (wasOpen) return; // la question va se fermer : on ne track que l'ouverture
        var q = title.querySelector("h3");
        pushDataLayer({ event: "faq", question: q ? q.textContent.trim() : "" });
      },
      true,
    );
  }

  // Listeners délégués : attachables immédiatement
  trackFilterUse();
  trackCompare();
  trackFaq();

  // Events au chargement de la page
  onReady(function () {
    trackViewItem();
    trackViewItemList();
    trackSearch();
  });
})();
